import React from "react";
import {
  resolveActionItemsFilterSummary,
  splitActionItemsSourceLabel,
} from "../actionItemHelpers";
import ActionItemsFilterChip from "./ActionItemsFilterChip";
import ActionItemsMeetingFilterChip from "./ActionItemsMeetingFilterChip";

/**
 * One removable chip per active filter (source, category, AI-assist, meeting), each with
 * a short headline and an optional muted subtext line.
 *
 * @example
 * // Input — Gmail source + Work category selected
 * <ActionItemsActiveFilterBar
 *   workflowFilterId="wf-gmail"
 *   categoryFilterId="Work"
 *   sourceNav={{ workflowEntries: [{ id: "wf-gmail", name: "Gmail - sam@co.com" }] }}
 *   onRemoveSource={() => {}}
 *   onRemoveCategory={() => {}}
 * />
 * // Output — two chips: "Gmail" (sub "sam@co.com") and "Work"
 */
const ActionItemsActiveFilterBar = ({
  workflowFilterId = "",
  categoryFilterId = "",
  assistFilterId = "",
  agingFilterActive = false,
  starredFilterActive = false,
  meetingLabel = "",
  meetingSourceLabel = "",
  meetingSources = [],
  sourceNav,
  onRemoveSource,
  onRemoveCategory,
  onRemoveAssist,
  onRemoveAging,
  onRemoveStarred,
  onRemoveMeeting,
  onRemoveMeetingSource,
  onClear,
  className = "",
}) => {
  const { sourceLabel, categoryLabel, assistLabel, agingLabel, starredLabel, isActive } =
    resolveActionItemsFilterSummary({
      workflowFilterId,
      categoryFilterId,
      assistFilterId,
      agingFilterActive,
      starredFilterActive,
      sourceNav,
    });

  const hasMeeting = Boolean(meetingLabel);
  if (!isActive && !hasMeeting) return null;

  const chips = [];
  if (sourceLabel) {
    const { main, sub } = splitActionItemsSourceLabel(sourceLabel);
    chips.push({
      key: "source",
      main,
      sub,
      onRemove: onRemoveSource || onClear,
      ariaLabel: `Remove ${main} filter`,
    });
  }
  if (categoryLabel) {
    chips.push({
      key: "category",
      main: categoryLabel,
      sub: "",
      onRemove: onRemoveCategory || onClear,
      ariaLabel: `Remove ${categoryLabel} filter`,
    });
  }
  if (assistLabel) {
    chips.push({
      key: "assist",
      main: assistLabel,
      sub: "",
      onRemove: onRemoveAssist || onClear,
      ariaLabel: `Remove ${assistLabel} filter`,
    });
  }
  if (agingLabel) {
    chips.push({
      key: "aging",
      main: agingLabel,
      sub: "",
      onRemove: onRemoveAging || onClear,
      ariaLabel: `Remove ${agingLabel} filter`,
    });
  }
  if (starredLabel) {
    chips.push({
      key: "starred",
      main: starredLabel,
      sub: "",
      onRemove: onRemoveStarred || onClear,
      ariaLabel: `Remove ${starredLabel} filter`,
    });
  }
  // A meeting spanning several sources nests one removable pill per source inside a
  // single chip; with just one (or zero) sources it's a plain chip with a subtext line.
  const hasMultiSourceMeeting = hasMeeting && meetingSources.length > 1;
  if (hasMeeting && !hasMultiSourceMeeting) {
    const { main: meetingSourceMain } = splitActionItemsSourceLabel(meetingSourceLabel);
    chips.push({
      key: "meeting",
      main: meetingLabel,
      sub: meetingSourceMain,
      onRemove: onRemoveMeeting || onClear,
      ariaLabel: `Remove ${meetingLabel} meeting filter`,
    });
  }

  return (
    <>
      {chips.map((chip) => (
        <ActionItemsFilterChip
          key={chip.key}
          main={chip.main}
          sub={chip.sub}
          onRemove={chip.onRemove}
          ariaLabel={chip.ariaLabel}
          className={className}
        />
      ))}
      {hasMultiSourceMeeting ? (
        <ActionItemsMeetingFilterChip
          label={meetingLabel}
          sources={meetingSources}
          onRemoveAll={onRemoveMeeting || onClear}
          onRemoveSource={onRemoveMeetingSource}
          className={className}
        />
      ) : null}
    </>
  );
};

export default ActionItemsActiveFilterBar;
